import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { StaffingBadge, addDays, fmtDay, ymd, errMsg } from './porterUi';

const jobStatus = (j) => j.job_status === 'done' || j.completed_at ? ['Completed', 'bg-slate-100 text-slate-700']
  : ['on_the_way', 'arrived'].includes(j.job_status) ? ['Working', 'bg-emerald-100 text-emerald-800']
  : j.crew.length ? ['Assigned', 'bg-blue-100 text-blue-800'] : ['Not crewed', 'bg-red-100 text-red-800'];

export const WhoWithWho = ({ authFetch, date, setDate }) => {
  const [days, setDays] = useState(1);
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    authFetch('get', `/admin/crew-board?start=${date}&days=${days}`).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  }, [authFetch, date, days]);
  useEffect(() => { load(); }, [load]);
  const drv = (id) => data?.drivers.find(d => d.id === id);

  return (
    <div data-testid="who-with-who">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
          {[[1, 'Day'], [7, 'Week']].map(([n, l]) => <button key={n} onClick={() => setDays(n)} className={`h-9 px-3 rounded text-xs font-bold ${days === n ? 'bg-[color:var(--mv-navy)] text-white' : 'text-slate-600'}`} data-testid={`rota-range-${l.toLowerCase()}`}>{l}</button>)}
        </div>
        <button onClick={() => setDate(addDays(date, -days))} className="h-10 w-10 rounded-md border border-slate-200 bg-white inline-flex items-center justify-center" aria-label="Previous" data-testid="rota-prev"><ChevronLeft className="w-4 h-4" /></button>
        <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid="rota-date" />
        <button onClick={() => setDate(addDays(date, days))} className="h-10 w-10 rounded-md border border-slate-200 bg-white inline-flex items-center justify-center" aria-label="Next" data-testid="rota-next"><ChevronRight className="w-4 h-4" /></button>
        <button onClick={() => setDate(ymd(new Date()))} className="h-10 px-3 rounded-md border border-slate-200 bg-white text-xs font-bold" data-testid="rota-today">Today</button>
      </div>
      {!data ? <Loader2 className="w-5 h-5 animate-spin" /> : data.dates.map(day => {
        const jobs = data.jobs.filter(j => j.booking.date === day);
        return (
          <section key={day} className="mb-5">
            <h3 className="font-display font-extrabold text-[color:var(--mv-navy)] mb-2">{fmtDay(day)} <span className="text-xs text-slate-500 font-normal">· {jobs.length} job{jobs.length === 1 ? '' : 's'}</span></h3>
            {jobs.length === 0 ? <div className="text-xs text-slate-500 mb-2">No jobs.</div> : (
              <>
                <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200 bg-white">
                  <table className="w-full text-sm" data-testid={`rota-table-${day}`}>
                    <thead><tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><th className="text-left p-2">Driver</th><th className="text-left p-2">Assigned porters</th><th className="text-left p-2">Vehicle</th><th className="text-left p-2">Job</th><th className="text-left p-2">Status</th><th className="text-left p-2">Crew</th></tr></thead>
                    <tbody>
                      {jobs.map(j => {
                        const [sl, sc] = jobStatus(j);
                        return (
                          <tr key={j.id} className="border-t border-slate-100" data-testid={`rota-row-${j.ref}`}>
                            <td className="p-2 font-bold text-[color:var(--mv-navy)]">{drv(j.driver_id)?.name || 'Unassigned'}</td>
                            <td className="p-2">{j.crew.length ? j.crew.map(c => c.porter.name).join(', ') : <span className="text-slate-400">—</span>}</td>
                            <td className="p-2">{j.van ? `${j.van.name || j.van.reg}` : <span className="text-slate-400">—</span>}{j.van?.off_road && <span className="ml-1 text-red-700 text-[10px] font-black">OFF ROAD</span>}</td>
                            <td className="p-2">#{j.ref} · {j.booking.time}</td>
                            <td className="p-2"><span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${sc}`}>{sl}</span></td>
                            <td className="p-2"><StaffingBadge job={j} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="md:hidden space-y-2">
                  {jobs.map(j => {
                    const [sl, sc] = jobStatus(j);
                    return (
                      <div key={j.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm" data-testid={`rota-card-${j.ref}`}>
                        <div className="flex justify-between gap-2"><strong className="text-[color:var(--mv-navy)]">{drv(j.driver_id)?.name || 'Unassigned'}</strong><span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${sc}`}>{sl}</span></div>
                        <div className="text-xs text-slate-600 mt-1">#{j.ref} · {j.booking.time} · {j.van ? (j.van.name || j.van.reg) : 'No van'}</div>
                        <div className="text-xs mt-1">{j.crew.length ? j.crew.map(c => c.porter.name).join(', ') : 'No porters'}</div>
                        <div className="mt-1"><StaffingBadge job={j} /></div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        );
      })}
    </div>
  );
};
